import React, { useState, useEffect } from "react";
import OnboardingLanding from "./Landing";
import OnboardingSnapshot from "./Snapshot";
import OnboardingAgreement from "./Agreement";
import { postOnboardingPage1, postOnboardingPage2, postOnboardingActivate, postDeployQuizzes } from "../../api/overviewApi";
import { Dialog, DialogTitle, DialogContent, Typography } from "@mui/material";

/**
 * OnboardingFlow orchestrates the three-step onboarding process:
 * 1. Landing page
 * 2. Simple snapshot
 * 3. Data usage agreement (includes survey schedule selection)
 *
 * Props:
 * - open: boolean - whether the onboarding flow should be shown
 * - onComplete: (data) => Promise<void> - called when user completes all steps
 *   data = { notificationSelected: boolean, startWeek: string, endWeek: string }
 * - sessionInfo: object - session info with courseId
 * - videoUrl: string (optional) - URL for intro video
 */
export default function OnboardingFlow({
  open,
  onComplete,
  sessionInfo,
  videoUrl,
}) {
  const [step, setStep] = useState(1); // 1: landing, 2: snapshot, 3: agreement
  const [notificationSelected, setNotificationSelected] = useState(true);
  const [startWeek, setStartWeek] = useState("");
  const [endWeek, setEndWeek] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const ONBOARDING_CUTOFF = new Date("2026-08-24T00:00:00-07:00");
  const missedCutoff = new Date() > ONBOARDING_CUTOFF;
  const REOPEN_DATE = new Date("2026-08-30T00:00:00-07:00");
  const REOPEN_DATE_DISPLAY = REOPEN_DATE.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setNotificationSelected(true);
      setStartWeek("");
      setEndWeek("");
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const handleLandingContinue = async () => {
    try {
      await postOnboardingPage1(sessionInfo.courseId);
    } catch (err) {
      console.error("Error in postOnboardingPage1:", err);
    } finally {
      setStep(2);
    }   
  };

  const handleSnapshotContinue = async () => {
    try {
      await postOnboardingPage2(sessionInfo.courseId);
    } catch (err) {
      console.error("Error in postOnboardingPage2:", err);
    } finally {
      setStep(3);
    }
  };

  const handleSnapshotBack = () => {
    setStep(1);
  };

  const handleAgreementBack = () => {
    setStep(2);
  };

  const handleScheduleChange = (newStartWeek, newEndWeek) => {
    setStartWeek(newStartWeek);
    setEndWeek(newEndWeek);
  };

  const handleAgreementAccept = async () => {
    setSaving(true);
    setError(null);

    try {
      await postOnboardingActivate(sessionInfo.courseId, notificationSelected, startWeek, endWeek);
      await postDeployQuizzes(sessionInfo.courseId);
      await onComplete({ notificationSelected, startWeek, endWeek });
      // onComplete should close the dialog if successful
    } catch (err) {
      setError(err.message || "Failed to save preferences");
      setSaving(false);
    }
  };

  // if (missedCutoff) {
  //   return (
  //     open && (
  //       <Dialog open={open} onClose={() => {}}>
  //         <DialogTitle>Onboarding Paused</DialogTitle>
  //         <DialogContent>
  //           <Typography>
  //             Onboarding for this term is currently closed. If you're working with
  //             TLC, we'll walk you through it at Downstream info session on {REOPEN_DATE_DISPLAY}.
  //           </Typography>
  //         </DialogContent>
  //       </Dialog>
  //     )
  //   );
  // }

  return (
    <>
      <OnboardingLanding
        open={open && step === 1}
        onContinue={handleLandingContinue}
        videoUrl={videoUrl}
      />

      <OnboardingSnapshot
        open={open && step === 2}
        onClose={() => {}} // Prevent closing during onboarding
        onContinue={handleSnapshotContinue}
        onBack={handleSnapshotBack}
      />

      <OnboardingAgreement
        open={open && step === 3}
        onAccept={handleAgreementAccept}
        onBack={handleAgreementBack}
        saving={saving}
        error={error}
        initialPreferences={notificationSelected}
        onPreferenceChange={setNotificationSelected}
        initialStartWeek={startWeek}
        initialEndWeek={endWeek}
        onScheduleChange={handleScheduleChange}
      />
    </>
  );
}